import PPLLogo from 'img/shipping/logo_ppl.jpg';
import ZasilkovnaLogo from 'img/shipping/logo_zasilkovna.png';
import PPLDHLLogo from 'img/shipping/logo_ppl_dhl.png';
import DHLLogo from 'img/shipping/logo_dhl.png';
import ZasilkovnaBigLogo from 'img/shipping/logo_zasilkovna_big.png';
import GLSLogo from 'img/shipping/logo_gls.jpg';
import LiftagoLogo from 'img/shipping/logo_liftago.png';
import DPDLogo from 'img/shipping/dpd_logo.png';
import GeisLogo from 'img/shipping/logo_geis.png';
import { formatPrice, weekDays } from 'helpers/utils';

export const getShippingProviderName = (shippingProvider: Backend.Transport['shipping_provider']) => {
  switch (shippingProvider) {
    case 'RETINO_PPL':
    case 'CUSTOMER_RETINO_PPL':
      return gettext('PPL Parcelshop');
    case 'CUSTOMER_RETINO_GLS_DROPOFF':
    case 'MY_GLS_DROPOFF':
      return gettext('GLS Parcelshop');
    case 'CUSTOMER_RETINO_GLS_PICKUP':
    case 'CUSTOMER_RETINO_GLS_PICKUP_SK':
    case 'CUSTOMER_RETINO_GLS_PICKUP_HU':
      return gettext('GLS pickup');
    case 'ZASILKOVNA':
      return gettext('Zasilkovna');
    case 'GLS':
    case 'MY_GLS':
      return gettext('GLS pickup');
    case 'GLS_DROPOFF_PL':
      return gettext('GLS Parcelshop Poland');
    case 'PPL':
      return gettext('PPL pickup');
    case 'DPD_PICKUP':
    case 'CUSTOMER_PAID_RETINO_DPD_PICKUP':
      return gettext('DPD pickup');
    case 'DPD_DROPOFF':
    case 'CUSTOMER_PAID_RETINO_DPD_DROPOFF':
      return gettext('DPD dropoff');
    case 'LIFTAGO':
      return gettext('Liftago');
    case 'RETURN_CONNECT':
      return gettext('PPL Return Connect');
    case 'CUSTOM_OTHER_PROVIDER':
      return gettext('Custom shipping provider');
    case 'GEIS':
      return gettext('Geis');
    case 'DHL_FREIGHT':
      return gettext('DHL Freight');
    case 'DHL_DROPOFF':
      return gettext('DHL Parcelshop');
    default:
      return null;
  }
};

export const getShippingStatusName = (status: Backend.Transport['status']) => {
  switch (status) {
    case 'STATUS_INIT':
      return gettext('Allowed');
    case 'STATUS_PENDING_CONFIRMATION':
      return gettext('Pending confirmation');
    case 'STATUS_ACCEPTED':
      return gettext('Generated shipping label');
    case 'STATUS_SUBMITTED':
      return gettext('Delivered to the branch');
    case 'STATUS_TRANSPORT':
      return gettext('Supplied by the carrier');
    case 'STATUS_DELIVERED':
      return gettext('Delivered');
    case 'STATUS_UNDELIVERED':
      return gettext('Undelivered');
    case 'STATUS_CANCELED':
      return gettext('Cancelled');
    case 'STATUS_REFUNDED':
      return gettext('Refunded');
    case 'STATUS_UNKNOWN':
      return gettext('Unknown');
    default:
      return null;
  }
};

export const getShippingProviderLogo = (
  shippingProvider: Backend.Transport['shipping_provider'],
  shape: 'rect' | 'square' = 'square',
) => {
  switch (shippingProvider) {
    case 'RETINO_PPL':
    case 'CUSTOMER_RETINO_PPL':
    case 'PPL':
      return PPLLogo;
    case 'ZASILKOVNA':
      return shape === 'rect' ? ZasilkovnaBigLogo : ZasilkovnaLogo;
    case 'GLS':
    case 'MY_GLS':
    case 'MY_GLS_DROPOFF':
    case 'GLS_DROPOFF_PL':
    case 'CUSTOMER_RETINO_GLS_PICKUP':
    case 'CUSTOMER_RETINO_GLS_PICKUP_SK':
    case 'CUSTOMER_RETINO_GLS_PICKUP_HU':
    case 'CUSTOMER_RETINO_GLS_DROPOFF':
    case 'CUSTOMER_RETINO_GLS_DROPOFF_SK':
    case 'CUSTOMER_RETINO_GLS_DROPOFF_HU':
      return GLSLogo;
    case 'LIFTAGO':
      return LiftagoLogo;
    case 'DPD_DROPOFF':
    case 'DPD_PICKUP':
    case 'CUSTOMER_PAID_RETINO_DPD_DROPOFF':
    case 'CUSTOMER_PAID_RETINO_DPD_PICKUP':
      return DPDLogo;
    case 'RETURN_CONNECT':
      return PPLDHLLogo;
    case 'GEIS':
      return GeisLogo;
    case 'DHL_DROPOFF':
    case 'DHL_FREIGHT':
      return DHLLogo;
    default:
      return null;
  }
};

export const getShippingTypePrice = (item: BackendPublic.FormShippingType) =>
  item.shipping_provider_data.paid_by_customer && item.shipping_provider_data.customer_price
    ? item.shipping_provider_data.customer_price
    : { price: Number(item.displayed_price), currency: item.displayed_currency };

export const getShippingTypesPriceStr = (shippingTypes: readonly BackendPublic.FormShippingType[]) => {
  const allFree = !shippingTypes.some((item) => getShippingTypePrice(item).price);

  if (allFree) {
    return gettext('free');
  }

  const someFree = shippingTypes.some((item) => !getShippingTypePrice(item).price);

  const lowestPrice = shippingTypes.reduce(
    ({ price, currency }, item) =>
      getShippingTypePrice(item).price < price && getShippingTypePrice(item).price > 0
        ? { price: getShippingTypePrice(item).price, currency: getShippingTypePrice(item).currency }
        : { price, currency },
    { price: Infinity, currency: '' },
  );

  if (someFree) {
    return interpolate(
      gettext('free or from %(price)s'),
      { price: formatPrice(lowestPrice.price, lowestPrice.currency) },
      true,
    );
  }

  if (shippingTypes.length === 1) {
    return formatPrice(lowestPrice.price, lowestPrice.currency);
  }

  return interpolate(gettext('from %(price)s'), { price: formatPrice(lowestPrice.price, lowestPrice.currency) }, true);
};

export const condensateOpenHours = (
  dropOffBranch: BackendPublic.ShippingDropOffBranch | Backend.ShippingDropOffBranch,
): { day: string; openHours: string }[] => {
  let startDayId = 0;
  let endDayId = startDayId;
  const condensatedOpenHours: { day: string; openHours: string }[] = [];

  Object.keys(dropOffBranch.work_hours).forEach((dayId) => {
    const currentDayWorkHours = dropOffBranch.work_hours[dayId].flat();
    const nextDayWorkHours = dropOffBranch.work_hours[Number(dayId) + 1]
      ? dropOffBranch.work_hours[Number(dayId) + 1].flat()
      : null;
    if (
      (currentDayWorkHours &&
        nextDayWorkHours &&
        currentDayWorkHours.length === nextDayWorkHours.length &&
        currentDayWorkHours.every((value, index) => value === nextDayWorkHours[index])) ||
      (!currentDayWorkHours && !nextDayWorkHours && endDayId < weekDays.length - 1)
    ) {
      endDayId += 1;
    } else {
      const daysName =
        startDayId === endDayId
          ? weekDays[startDayId].slice(0, 2)
          : `${weekDays[startDayId].slice(0, 2)}\u2013${weekDays[endDayId].slice(0, 2)}`;

      condensatedOpenHours.push({
        day: daysName,
        openHours:
          (dropOffBranch.work_hours[startDayId] &&
            dropOffBranch.work_hours[startDayId].map((hour) => hour.join('\u2013')).join(', ')) ||
          gettext('closed'),
      });
      endDayId += 1;
      startDayId = endDayId;
    }
  });
  return condensatedOpenHours;
};

export default {
  getShippingProviderLogo,
  getShippingProviderName,
  getShippingTypePrice,
  getShippingTypesPriceStr,
  condensateOpenHours,
};
